import TimelinePlugin from "wavesurfer.js/plugins/timeline";
import RegionsPlugin from "wavesurfer.js/plugins/regions";
import { useCallback, useEffect, useRef, useState } from "react";
import { getSegmentColorWithData } from "../helpers/AppHelper.jsx";
import WaveSurfer from "wavesurfer.js";
import { Slider, Tooltip } from "@heroui/react";
import PageLoader from '../components/PageLoader.jsx';
import _ from "lodash";
import audioContextManager from "../utils/audioContextManager.js";

const audioBufferCache = new Map();
const pendingFetches = new Map();

export default function TimelineEditor({
    audio,
    segments,
    segmentData,
    scrollToPosition,
    fps,
    playerRef,
    onRegionUpdated,
    updateLoading,
    currentPosition
}) {
    const editorContainer = useRef(null);
    const editorInstance = useRef(null);
    const regionsPlugin = useRef(null);
    const mediaElementRef = useRef(null);
    const [isReady, setIsReady] = useState(false);
    const onRegionUpdatedRef = useRef(onRegionUpdated);
    const isInteractingRef = useRef(false);
    const wasPlayingRef = useRef(false);

    const [isLoading, setIsLoading] = useState(true);
    const [timelineUpdated, setTimelineUpdated] = useState(false);

    const interactionPropsRef = useRef({ fps, autoScrollToSegment: () => { } });

    const autoScrollToSegment = useCallback((newTime) => {
        const closestSegmentIndex = segments.findIndex((segment) => {
            const startTime = segment?.start;
            const endTime = segment?.end;
            return newTime >= startTime && newTime <= endTime;
        });

        if (typeof scrollToPosition !== "function") return;

        if (closestSegmentIndex === -1) {
            scrollToPosition(segments.length - 1);
        } else {
            scrollToPosition(closestSegmentIndex);
        }
    }, [segments, scrollToPosition]);

    useEffect(() => {
        onRegionUpdatedRef.current = onRegionUpdated;
        interactionPropsRef.current = { fps, autoScrollToSegment };
    }, [onRegionUpdated, fps, autoScrollToSegment]);

    const seekRafRef = useRef(null);

    const performSeek = useCallback((time) => {
        if (!playerRef?.current) return;

        const { fps: currentFps, autoScrollToSegment: currentAutoScroll } = interactionPropsRef.current;

        // Always update scroll immediately for responsiveness
        currentAutoScroll(time);

        // Throttle video seeking using RAF to prevent overwhelming the player
        if (seekRafRef.current) cancelAnimationFrame(seekRafRef.current);

        seekRafRef.current = requestAnimationFrame(() => {
            if (playerRef.current) {
                playerRef.current.seekTo(time * currentFps);
            }
            seekRafRef.current = null;
        });
    }, [playerRef]);

    useEffect(() => {
        const container = editorContainer.current;
        if (!container) return;

        const onPointerDown = () => {
            isInteractingRef.current = true;
            if (playerRef.current?.isPlaying()) {
                wasPlayingRef.current = true;
                playerRef.current.pause();
            } else {
                wasPlayingRef.current = false;
            }
        };

        const onGlobalPointerUp = () => {
            if (isInteractingRef.current) {
                setTimeout(() => {
                    isInteractingRef.current = false;
                    if (wasPlayingRef.current && playerRef.current) {
                        playerRef.current.play();
                    }
                    wasPlayingRef.current = false;
                }, 200);
            }
        };

        container.addEventListener('pointerdown', onPointerDown);
        window.addEventListener('pointerup', onGlobalPointerUp);

        return () => {
            container.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('pointerup', onGlobalPointerUp);
        };
    }, []);

    const renderWaveformSegment = useCallback((
        audioUrl, canvas, beforeTime = 0, afterTime = 0,
        segmentDuration = 0, audioDuration = 0,
        beforeColor = '#4CAF50', afterColor = '#ff0080', gap = 0
    ) => {
        const ctx = canvas.getContext('2d');
        const currentSegmentColor = getSegmentColorWithData(segmentDuration, audioDuration);

        const draw = (audioBuffer) => {
            drawWaveform(ctx, audioBuffer, canvas, beforeTime, afterTime, currentSegmentColor, beforeColor, afterColor, gap);
        };

        if (pendingFetches.has(audioUrl)) {
            pendingFetches.get(audioUrl).then(draw);
            return;
        }

        if (audioBufferCache.has(audioUrl)) {
            draw(audioBufferCache.get(audioUrl));
            return;
        }

        const fetchPromise = fetch(audioUrl)
            .then(response => response.arrayBuffer())
            .then(arrayBuffer => {
                const audioContext = audioContextManager.getContext();
                if (!audioContext) throw new Error('Failed to get AudioContext');
                return audioContext.decodeAudioData(arrayBuffer);
            })
            .then(audioBuffer => {
                audioBufferCache.set(audioUrl, audioBuffer);
                pendingFetches.delete(audioUrl);
                draw(audioBuffer);
                return audioBuffer;
            })
            .catch(err => {
                console.error('Error rendering waveform:', err);
                pendingFetches.delete(audioUrl);
            });

        pendingFetches.set(audioUrl, fetchPromise);
    }, []);

    function drawWaveform(ctx, audioBuffer, canvas, beforeTime, afterTime, currentSegmentColor, beforeColor, afterColor, gap) {
        const sampleRate = audioBuffer.sampleRate;
        const rawData = audioBuffer.getChannelData(0);

        const canvasWidth = canvas.width;
        const canvasHeight = canvas.height;

        const beforeSamples = Math.floor(sampleRate * beforeTime);
        const afterSamples = Math.floor(sampleRate * afterTime);
        const totalSamples = beforeSamples + rawData.length + afterSamples;

        const effectiveWidth = canvasWidth - gap * 2;
        const samplesPerPixel = Math.max(1, Math.floor(totalSamples / effectiveWidth));
        const middleY = canvasHeight / 1.7;

        ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        const radius = 20;
        ctx.beginPath();
        ctx.moveTo(radius, 0);
        ctx.lineTo(canvasWidth - radius, 0);
        ctx.quadraticCurveTo(canvasWidth, 0, canvasWidth, radius);
        ctx.lineTo(canvasWidth, canvasHeight - radius);
        ctx.quadraticCurveTo(canvasWidth, canvasHeight, canvasWidth - radius, canvasHeight);
        ctx.lineTo(radius, canvasHeight);
        ctx.quadraticCurveTo(0, canvasHeight, 0, canvasHeight - radius);
        ctx.lineTo(0, radius);
        ctx.quadraticCurveTo(0, 0, radius, 0);
        ctx.closePath();
        ctx.clip();

        ctx.beginPath();
        ctx.moveTo(0, middleY);
        ctx.lineTo(canvasWidth, middleY);
        ctx.strokeStyle = '#9f9f9f';
        ctx.lineWidth = 1;
        ctx.stroke();

        for (let i = 0; i < effectiveWidth; i++) {
            const startSample = i * samplesPerPixel;
            let min = 1.0, max = -1.0;
            let isAudio = false;

            for (let j = 0; j < samplesPerPixel; j++) {
                const index = startSample + j;
                let sample = 0;

                if (index >= beforeSamples && index < beforeSamples + rawData.length) {
                    sample = rawData[index - beforeSamples];
                    isAudio = true;
                }

                if (sample < min) min = sample;
                if (sample > max) max = sample;
            }

            const x = i + gap;

            if (isAudio) {
                const y1 = (1 + min) * 0.55 * canvasHeight;
                const y2 = (1 + max) * 0.6 * canvasHeight;
                ctx.fillStyle = currentSegmentColor?.color || '#ff0000';
                ctx.fillRect(x, y1, 1, y2 - y1);
            } else {
                const percent = i / effectiveWidth;
                const audioStart = beforeSamples / totalSamples;
                ctx.fillStyle = percent > audioStart ? afterColor : beforeColor;
                ctx.fillRect(x, canvasHeight / 2.08, 1, 4);
            }
        }
    }

    // 3: Prevent Multiple RAF Loops for the same Region ---
    const createMiniWaveOverlay = useCallback((region, beforeTime, afterTime, audioFileUrl, segmentDuration, audioDuration) => {
        const overlayId = `mini_wave_overlay_${region.id}`;

        // Stop if we are already processing this specific region to avoid stacking loops
        if (region._isOverlayProcessing) return;
        region._isOverlayProcessing = true;

        function tryAttachWave() {
            const regionEl = region.element;
            // Additional safety: if region was removed while waiting, stop.
            if (!regionsPlugin.current || !regionsPlugin.current.getRegions().find(r => r.id === region.id)) {
                region._isOverlayProcessing = false;
                return;
            }

            if (!regionEl) {
                requestAnimationFrame(tryAttachWave);
                return;
            }

            const regionWidth = regionEl.clientWidth || regionEl.innerWidth;
            if (!regionWidth || regionWidth === 0) {
                requestAnimationFrame(tryAttachWave);
                return;
            }

            // Cleanup logic
            const existingOverlay = regionEl.querySelector(`#${overlayId}`);
            if (existingOverlay) {
                existingOverlay.remove();
            }

            const waveContainer = document.createElement('div');
            waveContainer.id = overlayId;
            waveContainer.style.position = 'absolute';
            waveContainer.style.bottom = '2px';
            waveContainer.style.left = '0';
            waveContainer.style.width = '100%';
            waveContainer.style.height = '20px';
            waveContainer.style.background = 'rgba(0, 0, 0, 0.0)';
            waveContainer.style.zIndex = '1';
            waveContainer.style.overflow = 'hidden';
            waveContainer.style.pointerEvents = 'none';

            const canvas = document.createElement('canvas');
            canvas.width = regionWidth;
            canvas.height = 20;
            waveContainer.appendChild(canvas);
            regionEl.appendChild(waveContainer);

            renderWaveformSegment(audioFileUrl, canvas, beforeTime, afterTime, segmentDuration, audioDuration);
            region._isOverlayProcessing = false;
        }

        requestAnimationFrame(tryAttachWave);
    }, [renderWaveformSegment]);

    const updateSegment = useCallback((region, newStart, newEnd) => {
        if (typeof onRegionUpdatedRef.current === 'function') {
            onRegionUpdatedRef.current(region.id, newStart, newEnd);
            setTimelineUpdated(true);
        }

        const segment = segments.find(s => String(s.id) === String(region.id));

        if (segment && region.element) {
            const segmentDuration = _.round(newEnd - newStart, 2);
            const extraDurationForEnd = _.round(segmentDuration - segment?.duration, 2);

            const regionEl = region.element;
            const overlayId = `mini_wave_overlay_${region.id}`;
            const oldOverlay = regionEl.querySelector(`#${overlayId}`);

            if (oldOverlay) {
                oldOverlay.remove();
            }

            setTimeout(() => {
                region._isOverlayProcessing = false;
                createMiniWaveOverlay(region, 0, extraDurationForEnd, segment.url, segmentDuration, segment?.duration);
            }, 50);
        }
    }, [segments, createMiniWaveOverlay]);

    const handleRightHandleDoubleClick = useCallback((region) => {
        if (playerRef?.current) {
            const currentIndex = segments.findIndex(s => String(s.id) === String(region.id));
            if (currentIndex === -1) return;

            const currentStart = region.start;
            const newEnd = currentStart + Math.max(segments[currentIndex]?.duration, 0);

            const nextSegmentStart = segments[currentIndex + 1]?.start
              ? segments[currentIndex + 1].start
              : (editorInstance.current?.getDuration() || 9999);

            if (newEnd > Math.max(nextSegmentStart - 0.2, 1)) {
                console.log('Auto duration exceeds next segment boundary');
                return;
            }

            if (newEnd > currentStart && newEnd) {
                region.setOptions({ end: newEnd });
                updateSegment(region, currentStart, newEnd);
            }
        }
    }, [fps, playerRef, segments, updateSegment]);

    const attachHandleDoubleClickHandlers = useCallback((region) => {
        const checkForElement = () => {
            const regionElement = region.element;
            if (!regionElement) {
                requestAnimationFrame(checkForElement);
                return;
            }

            const rightHandle = regionElement.querySelector('[part~="region-handle-right"]');

            if (rightHandle) {
                if (region._rightHandleClickHandler && region._rightHandleElement) {
                    region._rightHandleElement.removeEventListener('click', region._rightHandleClickHandler);
                }

                let clickCount = 0;
                let clickTimeout = null;

                const handleClick = (e) => {
                    e.stopPropagation();
                    clickCount++;

                    if (clickCount === 1) {
                        clickTimeout = setTimeout(() => {
                            clickCount = 0;
                        }, 300);
                    } else if (clickCount === 2) {
                        clearTimeout(clickTimeout);
                        clickCount = 0;
                        handleRightHandleDoubleClick(region);
                    }
                };

                rightHandle.addEventListener('click', handleClick);
                region._rightHandleClickHandler = handleClick;
                region._rightHandleElement = rightHandle;
            } else {
                requestAnimationFrame(checkForElement);
            }
        };

        requestAnimationFrame(checkForElement);
    }, [handleRightHandleDoubleClick]);

    useEffect(() => {
        if (!regionsPlugin.current || !isReady) return;

        const allCurrentRegions = regionsPlugin.current.getRegions();
        const incomingIds = new Set(segments.map(s => String(s.id)));

        allCurrentRegions.forEach(region => {
            if (!incomingIds.has(String(region.id))) {
                if (region.element) {
                    const overlay = region.element.querySelector(`#mini_wave_overlay_${region.id}`);
                    if (overlay) overlay.remove();
                }
                region.remove();
            }
        });

        const calculatedDuration = editorInstance.current.getDuration() || 100;

        segments.forEach((segment, index) => {
            let region = regionsPlugin.current.getRegions().find(r => String(r.id) === String(segment.id));

            if (region) {
                const newText = segment?.translated?.trim();
                if (region.content !== newText) {
                    region.setOptions({ content: newText });
                    const label = region.element?.querySelector('[part="region-content"]');
                    if (label) label.innerText = newText;
                }

                if (Math.abs(region.start - segment.start) > 0.01 || Math.abs(region.end - segment.end) > 0.01) {
                    region.setOptions({ start: segment.start, end: segment.end });
                }

                if (region._lastAudioUrl !== segment.url) {
                    const segmentDuration = _.round(segment.end - segment.start, 2);
                    const extraDuration = _.round(segmentDuration - segment?.duration, 2);

                    const overlayId = `mini_wave_overlay_${region.id}`;
                    if(region.element) {
                        const oldOverlay = region.element.querySelector(`#${overlayId}`);
                        if (oldOverlay) oldOverlay.remove();
                    }

                    if (segment.url) {
                        region._isOverlayProcessing = false;
                        createMiniWaveOverlay(region, 0, extraDuration, segment.url, (segmentDuration / 1000), (segment?.duration / 1000));
                    }
                    region._lastAudioUrl = segment.url;
                }

            } else {
                const prevEnd = index > 0 ? segments[index - 1]?.end : 0;
                const nextStart = index < segments.length - 1 ? segments[index + 1]?.start : calculatedDuration;

                region = regionsPlugin.current.addRegion({
                    id: String(segment.id),
                    channelIdx: index,
                    minLength: 0,
                    start: segment?.start,
                    end: segment?.end,
                    drag: true,
                    resize: true,
                    resizeEnd: true,
                    resizeStart: true,
                    color: 'rgba(230, 220, 255, 0.75)',
                    content: segment?.translated?.trim(),
                    textAlign: 'left',
                    overflow: 'hidden',
                    contentEditable: false,
                });

                region._originalStart = segment?.start;
                region._originalEnd = segment?.end;
                region._lastAudioUrl = segment.url;
                region.custom = {
                    startLimit: Math.max(prevEnd, 0),
                    endLimit: Math.min(nextStart, calculatedDuration)
                };

                const segmentDuration = _.round(segment.end - segment.start, 2);
                const extraDuration = _.round(segmentDuration - segment?.duration, 2);
                if (segment.url) {
                    createMiniWaveOverlay(region, 0, extraDuration, segment.url, (segmentDuration / 1000), (segment?.duration / 1000));
                }

                attachHandleDoubleClickHandlers(region);
            }
        });

    }, [segments, isReady, createMiniWaveOverlay, attachHandleDoubleClickHandlers]);


    // Cleanup Cache on Component
    useEffect(() => {
        return () => {
            audioBufferCache.clear();
            pendingFetches.clear();
        };
    }, []);

    useEffect(() => {
        if (!editorContainer.current || editorInstance.current) return;

        setIsLoading(true);

        const audioContext = audioContextManager.getContext();
        if (audioContext && audioContext.state === 'suspended') {
            audioContext.resume().catch(err => console.warn(err));
        }

        const audioEl = document.createElement('audio');
        audioEl.src = audio;
        audioEl.crossOrigin = "anonymous";
        mediaElementRef.current = audioEl;

        const wavesurfer = WaveSurfer.create({
            container: editorContainer.current,
            media: audioEl,
            width: "100%",
            height: 80,
            waveColor: "rgba(0,0,0,0.3)",
            minPxPerSec: 30,
            barWidth: 1,
            barHeight: 0.5,
            barAlign: "bottom",
            barGap: 2,
            cursorWidth: 2,
            cursorColor: '#5A0AE0',
            hideScrollbar: false,
            autoCenter: false,
            interact: true,
            normalize: false,
            autoScroll: true,
            backend: 'MediaElement',
            partialRender: true,
            plugins: [
                TimelinePlugin.create({
                    insertPosition: 'beforebegin',
                    primaryLabelInterval: 5,
                })
            ]
        });

        const regions = RegionsPlugin.create();
        wavesurfer.registerPlugin(regions);

        editorInstance.current = wavesurfer;
        regionsPlugin.current = regions;

        regions.on("region-clicked", (region, e) => {
            e.preventDefault();
            const duration = region.end - region.start;
            const bbox = region.element.getBoundingClientRect();
            const x = e.clientX - bbox.left;
            const progress = Math.max(0, Math.min(1, x / bbox.width));
            const time = region.start + progress * duration;
            performSeek(time);
        });

        wavesurfer.on("interaction", (time) => {
            performSeek(time);
        });

        wavesurfer.once("ready", () => {
            setIsReady(true);
            setIsLoading(false);
        });

        wavesurfer.on('error', (err) => {
            console.error("WaveSurfer error:", err);
            setIsLoading(false);
        });

        regions.on('region-updated', (region) => {
            region._originalStart = region.start;
            region._originalEnd = region.end;
            updateSegment(region, region.start, region.end);
        });

        return () => {
            setIsReady(false);
            if (editorInstance?.current) {
                editorInstance.current.destroy();
                editorInstance.current = null;
            }
            if (editorContainer.current) {
                editorContainer.current.innerHTML = '';
            }
            if (mediaElementRef.current) {
                mediaElementRef.current.src = "";
                mediaElementRef.current = null;
            }
            // DO NOT clear the global cache here, or you undo the fix.
        };
    }, [audio]);

    useEffect(() => {
        const player = playerRef?.current;
        if (!player) return;

        const handleFrameUpdate = (e) => {
            if (isInteractingRef.current) return;

            const targetTime = e?.detail?.frame / fps;
            const currentTime = editorInstance.current?.getCurrentTime();

            if (Math.abs(currentTime - targetTime) > 0.001) {
                editorInstance.current.setTime(targetTime);
            }
        };

        player.addEventListener("frameupdate", handleFrameUpdate);

        return () => {
            player.removeEventListener("frameupdate", handleFrameUpdate);
        };
    }, [playerRef, fps]);

    useEffect(() => {
        if (isReady && currentPosition > 0) {
            editorInstance?.current?.setTime(currentPosition);
        }
    }, [isReady, currentPosition]);

    useEffect(() => {
        setTimelineUpdated(false);
    }, [segmentData]);

    useEffect(() => {
        const container = editorContainer.current;
        if (!isReady || !container) return;

        const handleWheel = (e) => {
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {

                let scrollContainer = null;
                scrollContainer = container.querySelector('.scroll');

                if (!scrollContainer) {
                    const shadowHost = Array.from(container.children).find(child => child.shadowRoot);
                    if (shadowHost) {
                        scrollContainer = shadowHost.shadowRoot.querySelector('.scroll');
                    }
                }

                if (scrollContainer && scrollContainer.scrollWidth > scrollContainer.clientWidth) {
                    e.preventDefault();
                    scrollContainer.scrollLeft += e.deltaY;
                }
            }
        };

        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, [isReady]);

    return (
        <>
            {(isLoading) && (
                <div
                    className="w-full h-full flex grow items-center justify-center absolute left-0 top-0 bg-white/10 backdrop-blur-[2px] z-30 transition-all duration-300">
                    <PageLoader />
                </div>
            )}
            <div className="text-right w-full relative flex justify-end ">
                {timelineUpdated && (
                    <div onClick={() => {
                        setTimelineUpdated(false);
                        queryClient.refetchQueries({
                            queryKey: ['get-projects']
                        });
                    }} className="absolute left-2 -top-1">
                        <Tooltip content="reset">
                            <i className="fa-solid text-gray-800 cursor-pointer fa-rotate"></i>
                        </Tooltip>
                    </div>
                )}

                <Slider
                    aria-label="Scale"
                    className="max-w-40"
                    minValue={10}
                    defaultValue={50}
                    size="sm"
                    onChangeEnd={(value) => {
                        if (editorInstance?.current) {
                            editorInstance?.current?.zoom(value);
                        }
                    }}
                />
            </div>
            <div className="wavesurfer-container" ref={editorContainer}></div>
        </>
    );
}